import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Radio, Row, Col, Button, Modal, Menu, Typography, Input, Carousel } from 'antd';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEraser, faArrowsLeftRight, faChevronLeft, faTrash, faArrowUpFromBracket, faUpDownLeftRight, faCopy, faEyeDropper, faFill, faPlusMinus, faMagnifyingGlass, faMagnifyingGlassPlus, faMagnifyingGlassMinus, faLink, faDownload, faCheckCircle, faBolt, faWandMagicSparkles, faLayerGroup, faImages, faExpand, faHeadset } from "@fortawesome/free-solid-svg-icons";
import { parse, stringify } from 'svgson';
import uploadFile from '../../../helpers/s3-upload';
import { Divide } from 'react-feather';
import { appendUTMToURL } from '../../../helpers/utmTracking';
import { t } from '../../../i18n';
const { Paragraph, Title } = Typography;

function ExportMenu(props) {
  const [activeMenuItem, setActiveMenuItem] = useState('1'); // set initial active menu item to 1
  const [shareLink, setShareLink] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [countdown, setCountdown] = useState(30); // 30 seconds countdown
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const hasDownloadedRef = useRef(false);

  const handleMenuClick = (event) => {
    setActiveMenuItem(event.key); // update active menu item when a menu item is clicked
  };

  // Start download countdown automatically when modal opens
  useEffect(() => {
    if (props.isShareModalVisible && !isDownloading && !downloadComplete) {
      setIsDownloading(true);
      setCountdown(30);

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(countdownIntervalRef.current);
            startDownload();
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);

      // Cleanup on unmount
      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      };
    }
  }, [props.isShareModalVisible]);

  // Reset state when modal closes
  useEffect(() => {
    if (!props.isShareModalVisible) {
      setIsDownloading(false);
      setDownloadComplete(false);
      setCountdown(30);
      hasDownloadedRef.current = false;
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }
  }, [props.isShareModalVisible]);

  const startDownload = () => {
    // Prevent duplicate downloads
    if (hasDownloadedRef.current) {
      return;
    }
    hasDownloadedRef.current = true;
    
    const svgString = stringify(props.svgJoined);
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = url;
    a.download = "trace.svg";
    a.click();
    a.parentNode.removeChild(a);
    setIsDownloading(false);
    setDownloadComplete(true);
  };

  const handleDownloadAgain = () => {
    // Instant download, no countdown - bypass the guard for manual downloads
    const svgString = stringify(props.svgJoined);
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = url;
    a.download = "trace.svg";
    a.click();
    a.parentNode.removeChild(a);
  };

  // Custom graphic components for carousel slides
  const InstantDownloadGraphic = useMemo(() => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '30px'
    }}>
      {/* Left Card - 30s with timer */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '40px 50px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        position: 'relative'
      }}>
        {/* Timer emoji in corner */}
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          fontSize: '28px'
        }}>
          ⏱️
        </div>
        
        <div style={{
          fontSize: '72px',
          fontWeight: 'bold',
          color: '#999',
          position: 'relative'
        }}>
          30s
        </div>
        
        {/* Diagonal slash */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-15deg)',
          width: '140px',
          height: '8px',
          background: '#ff4d4f',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(255,77,79,0.6)'
        }} />
      </div>

      {/* Arrow */}
      <div style={{
        fontSize: '48px',
        color: 'white',
        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
        fontWeight: 'bold'
      }}>
        →
      </div>

      {/* Right Card - 0s with lightning */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '40px 50px',
        boxShadow: '0 6px 20px rgba(82,196,26,0.4)',
        position: 'relative',
        animation: 'pulse 2s ease-in-out infinite',
        border: '3px solid #52c41a'
      }}>
        {/* Lightning bolt in corner */}
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          fontSize: '28px'
        }}>
          ⚡
        </div>
        
        <div style={{
          fontSize: '72px',
          fontWeight: 'bold',
          color: '#52c41a',
          textShadow: '0 2px 4px rgba(82,196,26,0.3)'
        }}>
          0s
        </div>
      </div>
    </div>
  ), []);

  const AIEnhancementGraphic = useMemo(() => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '30px'
    }}>
      {/* Blurry/Pixelated Text */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '30px 45px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <div style={{
          fontSize: '52px',
          fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
          fontWeight: '400',
          fontStyle: 'italic',
          color: '#999',
          filter: 'blur(2px)',
          opacity: 0.7
        }}>
          {t("Blurry")}
        </div>
      </div>

      {/* Arrow */}
      <div style={{
        fontSize: '48px',
        color: 'white',
        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
        fontWeight: 'bold'
      }}>
        →
      </div>

      {/* Clear Text with glow */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '30px 45px',
        boxShadow: '0 6px 20px rgba(100,181,246,0.4)',
        animation: 'pulse 2s ease-in-out infinite',
        position: 'relative'
      }}>
        {/* Sparkle emoji in corner */}
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          fontSize: '28px'
        }}>
          ✨
        </div>
        
        <div style={{
          fontSize: '52px',
          fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
          fontWeight: '400',
          fontStyle: 'italic',
          color: '#1976d2',
          textShadow: '0 2px 4px rgba(25,118,210,0.3)'
        }}>
          {t("Crisp")}
        </div>
      </div>
    </div>
  ), []);

  const AIGenerationGraphic = useMemo(() => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '30px'
    }}>
      {/* Text Prompt Input */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '280px'
      }}>
        <div style={{
          fontSize: '11px',
          fontWeight: '600',
          color: '#666',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {t("Your Prompt:")}
        </div>
        <div style={{
          fontSize: '16px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: '#333',
          lineHeight: '1.5',
          fontStyle: 'italic'
        }}>
          {t('"A cute cat wearing sunglasses emoji"')}
        </div>
      </div>

      {/* Arrow */}
      <div style={{
        fontSize: '48px',
        color: 'white',
        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
        fontWeight: 'bold'
      }}>
        →
      </div>

      {/* Generated Image Placeholder */}
      <div style={{
        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 6px 20px rgba(248,143,177,0.4)',
        minWidth: '200px',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse 2s ease-in-out infinite',
        border: '3px solid #f48fb1',
        position: 'relative'
      }}>
        {/* Sparkle emoji in corner */}
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          fontSize: '28px'
        }}>
          ✨
        </div>
        
        {/* SVG Cat Icon */}
        <img 
          src={require('../../../assets/cute-cat-emoji.svg').default || require('../../../assets/cute-cat-emoji.svg')}
          alt="Generated Cat"
          style={{
            width: '120px',
            height: '120px',
            marginBottom: '12px'
          }}
        />
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          color: '#f48fb1',
          textAlign: 'center'
        }}>
          {t("Generated SVG")}
        </div>
      </div>
    </div>
  ), []);

  const BulkConversionGraphic = useMemo(() => {
    const imageEmojis = [
      { emoji: '⭐', color: '#FFD700' },
      { emoji: '♥️', color: '#FF6B9D' },
      { emoji: '◆', color: '#9C27B0' },
      { emoji: '■', color: '#2196F3' },
      { emoji: '▲', color: '#4CAF50' },
      { emoji: '●', color: '#FF5722' },
      { emoji: '✦', color: '#00BCD4' },
      { emoji: '⬟', color: '#FFC107' },
      { emoji: '⬢', color: '#E91E63' }
    ];
    
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '30px'
      }}>
        {/* Multiple Images Grid */}
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px',
            marginBottom: '12px',
            filter: 'blur(0.5px)',
            opacity: 0.85
          }}>
            {imageEmojis.map((item, i) => (
              <div
                key={i}
                style={{
                  width: '50px',
                  height: '50px',
                  background: item.color,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  color: 'white'
                }}
              >
                {item.emoji}
              </div>
            ))}
          </div>
          <div style={{
            fontSize: '11px',
          fontWeight: '600',
          color: '#666',
          textAlign: 'center'
        }}>
          {t("100+ Images")}
        </div>
        </div>

        {/* Arrow */}
        <div style={{
          fontSize: '48px',
          color: 'white',
          textShadow: '0 4px 8px rgba(0,0,0,0.3)',
          fontWeight: 'bold'
        }}>
          →
        </div>

        {/* SVG Files Stack */}
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 6px 20px rgba(77,182,172,0.4)',
          position: 'relative',
          animation: 'pulse 2s ease-in-out infinite',
          border: '3px solid #4db6ac'
        }}>
          {/* Stacked SVG files effect */}
          <div style={{ position: 'relative', width: '160px', height: '160px' }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: `${i * 8}px`,
                  left: `${i * 8}px`,
                  width: '140px',
                  height: '140px',
                  background: 'white',
                  border: '2px solid #4db6ac',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 3 - i
                }}
              >
                {i === 0 && (
                  <>
                    <div style={{
                      fontSize: '48px',
                      marginBottom: '8px',
                      color: imageEmojis[0].color
                    }}>
                      {imageEmojis[0].emoji}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#4db6ac'
                    }}>
                      .svg
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#4db6ac',
            textAlign: 'center',
            marginTop: '12px'
          }}>
            {t("All Converted")}
          </div>
        </div>
      </div>
    );
  }, []);

  const AIResizerGraphic = useMemo(() => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '30px'
    }}>
      {/* Small Blurry SVG */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '30px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '40px',
          filter: 'blur(1.5px)',
          opacity: 0.7
        }}>
          ⭐
        </div>
        <div style={{
          fontSize: '11px',
          fontWeight: '600',
          color: '#999'
        }}>
          {t("Small")}
        </div>
      </div>

      {/* Arrow */}
      <div style={{
        fontSize: '48px',
        color: 'white',
        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
        fontWeight: 'bold'
      }}>
        →
      </div>

      {/* Large Clear SVG */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '30px',
        boxShadow: '0 6px 20px rgba(186,104,200,0.4)',
        animation: 'pulse 2s ease-in-out infinite',
        border: '3px solid #ba68c8',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        position: 'relative'
      }}>
        {/* Sparkle emoji in corner */}
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          fontSize: '28px'
        }}>
          ✨
        </div>
        
        <div style={{
          width: '140px',
          height: '140px',
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '70px'
        }}>
          ⭐
        </div>
        <div style={{
          fontSize: '11px',
          fontWeight: '600',
          color: '#ba68c8'
        }}>
          {t("Large & Clear")}
        </div>
      </div>
    </div>
  ), []);

  const AIImageModifierGraphic = useMemo(() => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '30px'
    }}>
      {/* Prompt + Original Sad Dog - Vertically Stacked */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        minWidth: '240px'
      }}>
        {/* Prompt Box - On Top */}
        <div style={{
          background: '#f5f5f5',
          borderRadius: '8px',
          padding: '12px 16px',
          width: '100%',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
          border: '2px solid #d0d0d0'
        }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#666',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {t("Your Prompt:")}
          </div>
          <div style={{
            fontSize: '15px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            color: '#333',
            lineHeight: '1.5',
            fontStyle: 'italic'
          }}>
            {t('"make the dog happy"')}
          </div>
        </div>
        
        {/* Original Sad Dog Image - Below Prompt */}
        <div style={{
          width: '140px',
          height: '140px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <img 
            src={require('../../../assets/DogSad.svg').default || require('../../../assets/DogSad.svg')}
            alt="Sad Dog"
            style={{
              width: '120px',
              height: '120px'
            }}
          />
        </div>
      </div>

      {/* Arrow */}
      <div style={{
        fontSize: '48px',
        color: 'white',
        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
        fontWeight: 'bold'
      }}>
        →
      </div>

      {/* Modified Happy Dog */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 6px 20px rgba(79,195,247,0.4)',
        animation: 'pulse 2s ease-in-out infinite',
        border: '3px solid #4fc3f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        position: 'relative'
      }}>
        {/* Sparkle emoji in corner */}
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          fontSize: '28px'
        }}>
          ✨
        </div>
        
        <div style={{
          width: '160px',
          height: '160px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <img 
            src={require('../../../assets/DogHappy.svg').default || require('../../../assets/DogHappy.svg')}
            alt="Happy Dog"
            style={{
              width: '140px',
              height: '140px'
            }}
          />
        </div>
        
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          color: '#4fc3f7',
          textAlign: 'center'
        }}>
          {t("Modified Image")}
        </div>
      </div>
    </div>
  ), []);

  const SVGEditorToolsGraphic = useMemo(() => {
    const tools = [
      // Most unique/complex tools first (top row, left to right)
      { name: t('Smart Cut'), svgPath: require('../../../assets/Smartcut.svg').default || require('../../../assets/Smartcut.svg'), bgColor: '#f3e5f5' },
      { name: t('Vacuum'), svgPath: require('../../../assets/vacuum.svg').default || require('../../../assets/vacuum.svg'), bgColor: '#e1f5fe' },
      { name: t('Auto Fill'), svgPath: require('../../../assets/Autofill.svg').default || require('../../../assets/Autofill.svg'), bgColor: '#fce4ec' },
      { name: t('Color'), svgPath: require('../../../assets/ColorFill.svg').default || require('../../../assets/ColorFill.svg'), bgColor: '#f1f8e9' },
      { name: t('Erase'), svgPath: require('../../../assets/erase.svg').default || require('../../../assets/erase.svg'), bgColor: '#ffe0b2' },
      // Standard/basic tools (bottom row)
      { name: t('Crop'), svgPath: require('../../../assets/Crop.svg').default || require('../../../assets/Crop.svg'), bgColor: '#ffebee' },
      { name: t('Pan'), svgPath: require('../../../assets/Pan.svg').default || require('../../../assets/Pan.svg'), bgColor: '#fff3e0' },
      { name: t('Path'), svgPath: require('../../../assets/Path.svg').default || require('../../../assets/Path.svg'), bgColor: '#e8f5e9' },
      { name: t('Undo'), svgPath: require('../../../assets/Undo.svg').default || require('../../../assets/Undo.svg'), bgColor: '#e0f2f1' },
      { name: t('Redo'), svgPath: require('../../../assets/Redo.svg').default || require('../../../assets/Redo.svg'), bgColor: '#e0f2f1' }
    ];

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '12px',
        maxWidth: '550px'
      }}>
        {tools.map((tool) => (
          <div
            key={tool.name}
            style={{
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '10px',
              padding: '14px 8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              background: tool.bgColor,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src={tool.svgPath}
                alt={tool.name}
                style={{
                  width: '20px',
                  height: '20px'
                }}
              />
            </div>
            <div style={{
              fontSize: '10px',
              fontWeight: '600',
              color: '#555',
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}>
              {tool.name}
            </div>
          </div>
        ))}
      </div>
    );
  }, []);

  // Unified features with both list item and carousel slide data
  const proFeatures = [
    { 
      icon: faBolt, 
      text: t("Instant SVG downloads"), 
      color: "#00bcd4",
      title: t("Instant Downloads"),
      description: t("No waiting - download immediately"),
      bgGradient: "linear-gradient(135deg, #4dd0e1 0%, #00bcd4 100%)",
      customGraphic: InstantDownloadGraphic
      // imageUrl: "/path/to/instant-download-screenshot.png" // Add your image URL here
    },
    { 
      icon: faLayerGroup, 
      text: t("Unlock Full SVG Editor"), 
      color: "#9c6ade",
      title: t("Advanced SVG Editor"),
      description: t("Full layer control, colors, shapes, and more"),
      bgGradient: "linear-gradient(135deg, #b39ddb 0%, #9575cd 100%)",
      customGraphic: SVGEditorToolsGraphic
      // imageUrl: "/path/to/svg-editor-screenshot.png"
    },
    { 
      icon: faWandMagicSparkles, 
      text: t("AI Enhanced Conversions"), 
      color: "#64b5f6",
      title: t("AI Image Enhancement"),
      description: t("Enhance clarity and sharpness automatically"),
      bgGradient: "linear-gradient(135deg, #81d4fa 0%, #4fc3f7 100%)",
      customGraphic: AIEnhancementGraphic
      // imageUrl: "/path/to/ai-enhance-screenshot.png"
    },
    { 
      icon: faImages, 
      text: t("Commercial Use Image Generator"), 
      color: "#f48fb1",
      title: t("AI Image Generation"),
      description: t("Create stunning SVG images from text descriptions"),
      bgGradient: "linear-gradient(135deg, #f8bbd0 0%, #f48fb1 100%)",
      customGraphic: AIGenerationGraphic
      // imageUrl: "/path/to/ai-generation-screenshot.png"
    },
    { 
      icon: faImages, 
      text: t("Bulk SVG Conversion"), 
      color: "#4db6ac",
      title: t("Bulk Conversion"),
      description: t("Convert hundreds of images at once"),
      bgGradient: "linear-gradient(135deg, #80cbc4 0%, #4db6ac 100%)",
      customGraphic: BulkConversionGraphic
      // imageUrl: "/path/to/bulk-conversion-screenshot.png"
    },
    { 
      icon: faExpand, 
      text: t("AI Image Resizer"), 
      color: "#ba68c8",
      title: t("AI Image Resizer"),
      description: t("Resize and upscale images with AI precision"),
      bgGradient: "linear-gradient(135deg, #ce93d8 0%, #ba68c8 100%)",
      customGraphic: AIResizerGraphic
      // imageUrl: "/path/to/resizer-screenshot.png"
    },
    { 
      icon: faWandMagicSparkles, 
      text: t("AI Image Modifier"), 
      color: "#4fc3f7",
      title: t("AI Image Modifier"),
      description: t("Transform images with simple text prompts"),
      bgGradient: "linear-gradient(135deg, #81d4fa 0%, #4fc3f7 100%)",
      customGraphic: AIImageModifierGraphic
      // imageUrl: "/path/to/image-modifier-screenshot.png"
    },
  ];

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.05);
              opacity: 0.9;
            }
          }
          
          @keyframes glow {
            0%, 100% {
              box-shadow: 0 0 20px rgba(0,188,212,0.6), 0 0 40px rgba(0,188,212,0.4), 0 0 60px rgba(0,188,212,0.2);
            }
            50% {
              box-shadow: 0 0 30px rgba(0,188,212,0.8), 0 0 60px rgba(0,188,212,0.6), 0 0 90px rgba(0,188,212,0.4);
            }
          }
          
          /* Fix carousel slide heights and ensure consistency */
          .ant-carousel,
          .ant-carousel .slick-slider,
          .ant-carousel .slick-list,
          .ant-carousel .slick-track,
          .ant-carousel .slick-slide,
          .ant-carousel .slick-slide > div {
            height: 100% !important;
          }
          
          .ant-carousel .slick-slide {
            overflow: hidden;
          }
        `}
      </style>
    <Modal
        title={null}
      visible={props.isShareModalVisible}
      onOk={() => props.setIsShareModalVisible(false)}
      onCancel={() => props.setIsShareModalVisible(false)}
      footer={null}
        width={1000}
        closeIcon={<span style={{ fontSize: '20px' }}>×</span>}
      >
      <div style={{ padding: '12px 8px 8px' }}>
        {(isDownloading || downloadComplete) && (
          <>
            {/* Modern Header with Gradient */}
            <div style={{ 
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '20px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
            }}>
              {/* Left: Minimal Countdown */}
              {!downloadComplete && (
                <div style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>
                  {/* Progress ring background */}
                  <svg style={{ position: 'absolute', transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                    <circle
                      cx="26"
                      cy="26"
                      r="22"
                      fill="none"
                      stroke="#f0f0f0"
                      strokeWidth="4"
                    />
                    <circle
                      cx="26"
                      cy="26"
                      r="22"
                      fill="none"
                      stroke="url(#gradient)"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 22}`}
                      strokeDashoffset={`${2 * Math.PI * 22 * (countdown / 30)}`}
                      style={{ transition: 'stroke-dashoffset 1s linear' }}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#667eea" />
                        <stop offset="100%" stopColor="#764ba2" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '800',
                    color: '#667eea',
                    zIndex: 1,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  }}>
                    {countdown}
                  </div>
                </div>
              )}

               {/* Center: Question Text */}
               <div style={{ 
                 fontSize: '20px', 
                 fontWeight: '600', 
                 color: 'white',
                 lineHeight: '1.3',
                 flex: 1,
                 textShadow: '0 2px 12px rgba(0,0,0,0.15)',
                 fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
               }}>
                 {downloadComplete 
                   ? t('✓ Download Complete!') 
                   : t('Have you tried SVGTrace Pro yet?')}
               </div>

               {/* Right: Modern Trial Button */}
               <Button
                 type="primary"
                 href={appendUTMToURL("https://pro.svgtrace.com/signup", "export-menu-cta")} 
                 target="_blank" 
                 rel="noopener noreferrer"
                 style={{ 
                   background: 'white',
                   color: '#667eea',
                   border: 'none',
                   fontWeight: '700',
                   height: '52px',
                   padding: '0 32px',
                   fontSize: '16px',
                   transition: 'all 0.3s ease',
                   borderRadius: '26px',
                   whiteSpace: 'nowrap',
                   flexShrink: 0,
                   boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                   fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                 }}
                 onMouseEnter={(e) => {
                   e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                   e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.25)';
                   e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                 }}
                 onMouseLeave={(e) => {
                   e.currentTarget.style.transform = 'scale(1) translateY(0)';
                   e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                   e.currentTarget.style.background = 'white';
                 }}
               >
                 <span style={{ fontSize: '18px', marginRight: '6px' }}>🎉</span>
                 <span style={{ 
                   background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                   WebkitBackgroundClip: 'text',
                   WebkitTextFillColor: 'transparent',
                   backgroundClip: 'text',
                   fontWeight: '700'
                 }}>
                   {t("Try Pro for Free")}
                 </span>
               </Button>

              {/* Download Again Button (when complete) */}
              {downloadComplete && (
                <Button 
                  type="primary"
                  onClick={handleDownloadAgain}
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    fontWeight: '600',
                    height: '48px',
                    padding: '0 20px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    borderRadius: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    marginLeft: '8px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: '14px' }} />
                  <span>{t("Download Again")}</span>
                </Button>
              )}
            </div>

            {/* Horizontal Layout: Vertical Feature List + Carousel */}
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              alignItems: 'stretch'
            }}>
              {/* Vertical Feature List - Left Side */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '6px',
                minWidth: '200px',
                flexShrink: 0,
                height: '380px'
              }}>
                 {/* Small Header */}
                 <div style={{
                   fontSize: '13px',
                   fontWeight: '700',
                   background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                   WebkitBackgroundClip: 'text',
                   WebkitTextFillColor: 'transparent',
                   backgroundClip: 'text',
                   textAlign: 'center',
                   marginBottom: '6px',
                   textTransform: 'uppercase',
                   letterSpacing: '1.5px',
                   fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                 }}>
                   {t("What you're missing:")}
                 </div>
                {proFeatures.map((feature, index) => (
                  <div
                    key={index} 
                    onClick={() => {
                      setActiveSlide(index);
                      carouselRef.current?.goTo(index);
                    }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px 12px',
                      borderRadius: '10px',
                      transition: 'all 0.3s ease',
                      background: activeSlide === index 
                        ? `linear-gradient(135deg, ${feature.color}dd, ${feature.color})` 
                        : '#f5f5f5',
                      color: activeSlide === index ? 'white' : '#333',
                      boxShadow: activeSlide === index 
                        ? '0 3px 12px rgba(0,0,0,0.2)' 
                        : '0 2px 4px rgba(0,0,0,0.08)',
                      transform: activeSlide === index ? 'translateX(3px) scale(1.02)' : 'translateX(0) scale(1)',
                      border: activeSlide === index ? 'none' : '1px solid #e0e0e0',
                      fontWeight: activeSlide === index ? '600' : '500',
                      fontSize: '12px'
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: activeSlide === index ? 'rgba(255,255,255,0.25)' : feature.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.3s ease'
                    }}>
                      <FontAwesomeIcon 
                        icon={feature.icon} 
                        style={{ 
                          color: 'white', 
                          fontSize: '10px' 
                        }} 
                      />
                    </div>
                    <span style={{ lineHeight: '1.3' }}>{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Carousel - Right Side */}
              <div style={{ 
                flex: 1, 
                minWidth: 0, 
                height: '380px',
                borderRadius: '10px',
                overflow: 'hidden',
                boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
                background: '#fff'
              }}>
                 <Carousel 
                   key={props.isShareModalVisible ? 'carousel-active' : 'carousel-inactive'}
                   ref={carouselRef}
                   autoplay 
                   autoplaySpeed={3500}
                   beforeChange={(current, next) => setActiveSlide(next)}
                   dots={false}
                 >
                  {proFeatures.map((feature, index) => (
                    <div key={index}>
                      <div style={{
                        background: feature.imageUrl 
                          ? `url(${feature.imageUrl})` 
                          : feature.bgGradient,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        padding: '30px 24px',
                        height: '380px',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        color: 'white',
                        boxSizing: 'border-box'
                      }}>
                        {!feature.imageUrl && (
                          feature.customGraphic ? (
                            <div style={{ transform: 'scale(0.7)' }}>
                              {feature.customGraphic}
                            </div>
                          ) : (
                            <FontAwesomeIcon 
                              icon={feature.icon} 
                              style={{ 
                                fontSize: '64px', 
                                marginBottom: '16px',
                                opacity: 0.95,
                                filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.3))'
                              }} 
                            />
                          )
                        )}
                        <Title level={2} style={{ 
                          color: 'white', 
                          marginBottom: '10px', 
                          marginTop: '12px', 
                          fontWeight: 'bold',
                          fontSize: '28px',
                          textShadow: feature.imageUrl ? '0 3px 6px rgba(0,0,0,0.6)' : '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          {feature.title}
                        </Title>
                        <Paragraph style={{ 
                          color: 'white', 
                          fontSize: '16px', 
                          margin: 0,
                          maxWidth: '650px',
                          lineHeight: '1.5',
                          textShadow: feature.imageUrl ? '0 2px 4px rgba(0,0,0,0.6)' : '0 1px 2px rgba(0,0,0,0.2)'
                        }}>
                          {feature.description}
              </Paragraph>
                      </div>
                    </div>
                  ))}
                </Carousel>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
    </>
  );
}

export default ExportMenu;